import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { setSidebarStatus } from "actions/user";
import Arrow from "assets/images/leftArr.png";
import { get } from "api/index";
import "../Dashboard/dashboard.scss";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";

/**
 * Post Analytics page.
 *
 * Mirrors the structure of pages/Dashboard/Dashboard.jsx so analytics
 * looks part of the same product surface: same outer header bar +
 * Side Menu button, bg-surface-secondary main, container-fluid, and
 * Bootstrap row/col grid.
 *
 * Endpoints used (scope-aware on the backend — each user sees only
 * their accessible brands):
 *   GET /post-analytics/posts                → top posts table
 *   GET /post-analytics/posts/:id/history    → engagement-over-time
 *   GET /post-analytics/posts/:id/comments   → comments + sentiment
 *   GET /post-analytics/ideas                → idea ranking
 *   GET /post-analytics/platforms            → platform bar chart
 *   GET /post-analytics/brands               → per-brand ranking + top brand
 *   GET /post-analytics/brands/history       → per-brand engagement over time
 *   GET /post-analytics/engaged-commenters   → top engagers (14d)
 */

// Stable palette for the per-brand line charts — colours are assigned by the
// brand's index in the (engagement-sorted) brands list, cycling if there are
// more brands than colours.
const BRAND_COLORS = [
  "#0F62FE", "#22c55e", "#ef4444", "#a855f7", "#f59e0b",
  "#06b6d4", "#ec4899", "#84cc16", "#6366f1", "#14b8a6",
];

function StatCard({ label, value, accent = "#0F62FE", sub }) {
  return (
    <div className="dashboard-cards card border-0">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start">
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 13, color: "#64748b" }}>{label}</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: accent, lineHeight: 1.1, wordBreak: "break-word" }}>
              {value == null ? "—" : value}
            </div>
            {sub && <div style={{ fontSize: 12, color: "#94a3b8" }}>{sub}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

// Map a brand's rank (0 = highest engagement) within the engagement-sorted
// list onto a green→red hue. The top brand gets green (120°), the
// least-engaging gets red (0°), and everyone in between gets the proportional
// slice of the gradient. A lone brand is simply green — it is the top.
function rankHue(rank, total) {
  if (total <= 1) return 120;
  return Math.round(120 * (1 - rank / (total - 1)));
}

// One card in the "Top brands by engagement" strip. The hue overlay encodes
// the brand's rank so the strip reads as a left-to-right green→red gradient.
function BrandRankCard({ brand, rank, total }) {
  const hue = rankHue(rank, total);
  const count = brand.postCount ?? 0;
  return (
    <div
      data-testid="brand-rank-card"
      data-rank={rank}
      data-hue={hue}
      style={{
        flex: "0 0 auto",
        minWidth: 180,
        borderRadius: 8,
        padding: "14px 16px",
        background: `hsl(${hue}, 75%, 92%)`,
        border: `1px solid hsl(${hue}, 60%, 70%)`,
        borderLeft: `4px solid hsl(${hue}, 65%, 42%)`,
      }}
    >
      <div style={{ fontSize: 12, color: "#475569", fontWeight: 600 }}>#{rank + 1}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: "#0f172a", lineHeight: 1.15, wordBreak: "break-word" }}>
        {brand.name || "(unknown brand)"}
      </div>
      <div style={{ fontSize: 12, color: "#475569", marginTop: 2 }}>
        score {brand.score ?? 0} · {count} post{count === 1 ? "" : "s"}
      </div>
    </div>
  );
}

function ChartCard({ title, children, height = 220 }) {
  return (
    <div className="card border-0 mb-3">
      <div className="card-body">
        <p className="chart-header mb-2" style={{ fontSize: 14, fontWeight: 600 }}>
          {title}
        </p>
        <div style={{ width: "100%", height }}>{children}</div>
      </div>
    </div>
  );
}

// Card that hosts a long table — caps height + scrolls vertically so a
// 200-row table doesn't push the rest of the page off the screen.
function TableCard({ title, maxHeight = 360, children }) {
  return (
    <div className="card border-0 mb-3">
      <div className="card-body">
        <p className="chart-header mb-3" style={{ fontSize: 14, fontWeight: 600 }}>
          {title}
        </p>
        <div style={{ maxHeight, overflowY: "auto", overflowX: "auto" }}>
          {children}
        </div>
      </div>
    </div>
  );
}

const SENTIMENT_STYLE = {
  positive: { bg: "#dcfce7", fg: "#166534", label: "positive" },
  neutral:  { bg: "#e2e8f0", fg: "#475569", label: "neutral"  },
  negative: { bg: "#fee2e2", fg: "#991b1b", label: "negative" },
};

function SentimentBadge({ sentiment, intent }) {
  const s = SENTIMENT_STYLE[sentiment];
  if (!s) return null;
  return (
    <span
      data-testid="sentiment-badge"
      style={{
        marginLeft: 8,
        background: s.bg,
        color: s.fg,
        fontSize: 11,
        fontWeight: 600,
        padding: "1px 6px",
        borderRadius: 4,
        textTransform: "uppercase",
        letterSpacing: 0.3,
      }}
      title={intent ? `intent: ${intent}` : undefined}
    >
      {s.label}
      {intent && intent !== "other" ? ` · ${intent}` : ""}
    </span>
  );
}

function shortDate(d) {
  if (!d) return "";
  const dt = new Date(d);
  return isNaN(dt) ? "" : dt.toISOString().slice(0, 10);
}

// Columns of the "Top posts by engagement" table. Each carries a `sortValue`
// so a single comparator handles every column — text columns sort
// case-insensitively, numeric columns on their raw count. `numeric` also
// decides the default direction the first time a column is clicked
// (counts high→low, text A→Z) and right-aligns the cell.
const POST_COLUMNS = [
  { key: "title",     label: "Title",     numeric: false,
    sortValue: (p) => (p.title || p.text || "").toLowerCase() },
  { key: "brand",     label: "Brand",     numeric: false,
    sortValue: (p) => (p.brandName || "").toLowerCase() },
  { key: "idea",      label: "Idea",      numeric: false,
    sortValue: (p) => (p.ideaTitle || "").toLowerCase() },
  { key: "platforms", label: "Platforms", numeric: false,
    sortValue: (p) => (p.platforms || []).join(", ").toLowerCase() },
  { key: "likes",     label: "Likes",     numeric: true,
    sortValue: (p) => p.analytics?.likes ?? 0 },
  { key: "comments",  label: "Comments",  numeric: true,
    sortValue: (p) => p.analytics?.comments ?? 0 },
  { key: "shares",    label: "Shares",    numeric: true,
    sortValue: (p) => p.analytics?.shares ?? 0 },
  { key: "score",     label: "Score",     numeric: true,
    sortValue: (p) => p.analytics?.score ?? 0 },
];

export default function PostAnalytics() {
  const dispatch = useDispatch();

  const [posts, setPosts]         = useState([]);
  // Headline counts from the backend (the table caps at the top 200, but these
  // KPIs must reflect every post — see /post-analytics/posts). postsTotal =
  // every published post; postsAnalyzed = the subset with a real engagement
  // snapshot pulled (analytics.lastUpdated set).
  const [postsTotal, setPostsTotal] = useState(0);
  const [postsAnalyzed, setPostsAnalyzed] = useState(0);
  const [ideas, setIdeas]         = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [brands, setBrands]       = useState([]);
  // Brand-ranking platform lens: "all" = overall (engagement summed across
  // every platform), or a specific network to rank brands by that platform
  // alone. rankPlatforms is the list of networks that actually have data.
  const [rankPlatform, setRankPlatform]   = useState("all");
  const [rankPlatforms, setRankPlatforms] = useState([]);
  const [rankLoading, setRankLoading]     = useState(false);
  // Per-brand engagement time-series ({ brands, series }) + the brand the
  // "types of engagement per brand over time" chart is focused on.
  const [brandTimeline, setBrandTimeline] = useState({ brands: [], series: [] });
  const [selectedBrandId, setSelectedBrandId] = useState(null);
  const [engagedCommenters, setEngagedCommenters] = useState([]);
  const [history, setHistory]     = useState([]);
  const [historyPlatforms, setHistoryPlatforms] = useState([]);
  // Engagement-over-time platform filter: "all" = the cross-platform sum,
  // otherwise a single network's own numbers pulled from each snapshot's
  // perPlatform breakdown.
  const [platformFilter, setPlatformFilter] = useState("all");
  const [comments, setComments]   = useState([]);
  const [selectedPostId, setSelectedPostId] = useState(null);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);
  // Sort state for the "Top posts by engagement" table. key=null preserves the
  // backend order (score desc); clicking a header sets/toggles the column.
  const [postSort, setPostSort]   = useState({ key: null, dir: "desc" });

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [p, i, pl, br, bh, ec, rp] = await Promise.all([
        get("/post-analytics/posts"),
        get("/post-analytics/ideas"),
        get("/post-analytics/platforms"),
        get("/post-analytics/brands"),
        get("/post-analytics/brands/history"),
        get("/post-analytics/engaged-commenters"),
        get("/post-analytics/ranked-platforms"),
      ]);
      setPosts(p?.posts || []);
      setPostsTotal(p?.total ?? (p?.posts || []).length);
      setPostsAnalyzed(p?.analyzed ?? 0);
      setIdeas(i?.ideas || []);
      setPlatforms(pl?.platforms || []);
      setBrands(br?.brands || []);
      setRankPlatforms(rp?.platforms || []);
      setRankPlatform("all");
      const timeline = { brands: bh?.brands || [], series: bh?.series || [] };
      setBrandTimeline(timeline);
      // Focus the types-per-brand chart on the highest-engagement brand by
      // default (timeline.brands is already engagement-sorted by the backend).
      setSelectedBrandId(timeline.brands[0]?.brandId || null);
      setEngagedCommenters(ec?.commenters || []);
      // Auto-select the highest-scoring post so the chart isn't empty on load.
      const first = (p?.posts || [])[0];
      if (first) setSelectedPostId(first._id);
    } catch (e) {
      setError(e?.message || "Failed to load post analytics");
    } finally {
      setLoading(false);
    }
  }, []);

  // Re-rank brands when the platform lens changes. "all" is already loaded by
  // loadAll, so only a specific platform triggers a fetch.
  const loadBrandRanking = useCallback(async (platform) => {
    setRankLoading(true);
    try {
      const q = platform && platform !== "all" ? `?platform=${encodeURIComponent(platform)}` : "";
      const br = await get(`/post-analytics/brands${q}`);
      setBrands(br?.brands || []);
    } catch (e) {
      setBrands([]);
    } finally {
      setRankLoading(false);
    }
  }, []);

  const loadHistory = useCallback(async (postId) => {
    if (!postId) return;
    try {
      const res = await get(`/post-analytics/posts/${postId}/history`);
      const snaps = res?.snapshots || [];
      setHistory(snaps);
      // Prefer the platform list the backend computed; fall back to deriving it
      // from the snapshots so the filter still works against older payloads.
      const fromApi = Array.isArray(res?.platforms) ? res.platforms : null;
      const derived = Array.from(
        new Set(snaps.flatMap((s) => (s.perPlatform || []).map((p) => p.platform).filter(Boolean)))
      );
      setHistoryPlatforms(fromApi && fromApi.length ? fromApi : derived);
      setPlatformFilter("all");
    } catch (e) {
      setHistory([]);
      setHistoryPlatforms([]);
      setPlatformFilter("all");
    }
  }, []);

  const loadComments = useCallback(async (postId) => {
    if (!postId) return;
    try {
      const res = await get(`/post-analytics/posts/${postId}/comments`);
      setComments(res?.comments || []);
    } catch (e) {
      setComments([]);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);
  // Skip the very first render (loadAll already fetched "all"); only refetch
  // when the user actually switches the lens.
  const didMountRank = React.useRef(false);
  useEffect(() => {
    if (!didMountRank.current) { didMountRank.current = true; return; }
    loadBrandRanking(rankPlatform);
  }, [rankPlatform, loadBrandRanking]);
  useEffect(() => { loadHistory(selectedPostId); }, [selectedPostId, loadHistory]);
  useEffect(() => { loadComments(selectedPostId); }, [selectedPostId, loadComments]);

  const historyChartData = useMemo(
    () => (history || []).map((s) => {
      // "all" → the cross-platform aggregate stored flat on the snapshot.
      // A specific platform → that network's own slice from perPlatform
      // (zeros when the platform had no sample that cycle).
      const src =
        platformFilter === "all"
          ? s
          : (s.perPlatform || []).find((p) => p.platform === platformFilter) || {
              likes: 0, comments: 0, shares: 0, score: 0,
            };
      return {
        date: shortDate(s.timestamp),
        likes: src.likes || 0,
        comments: src.comments || 0,
        shares: src.shares || 0,
        score: src.score || 0,
      };
    }),
    [history, platformFilter]
  );

  // Chart can run for months — give each data point ~50px and let the
  // wrapping div scroll horizontally when the count exceeds what fits.
  const historyMinWidth = useMemo(
    () => Math.max(0, historyChartData.length * 50),
    [historyChartData.length]
  );

  const selectedPost = useMemo(
    () => posts.find((p) => p._id === selectedPostId),
    [posts, selectedPostId]
  );

  // Clicking a header sorts by that column; clicking the active column again
  // flips direction. A freshly-picked column starts high→low for counts and
  // A→Z for text.
  const handlePostSort = useCallback((key) => {
    setPostSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: POST_COLUMNS.find((c) => c.key === key)?.numeric ? "desc" : "asc" }
    );
  }, []);

  const sortedPosts = useMemo(() => {
    const col = POST_COLUMNS.find((c) => c.key === postSort.key);
    if (!col) return posts; // key=null → keep the backend's score-desc order
    const factor = postSort.dir === "asc" ? 1 : -1;
    return [...posts].sort((a, b) => {
      const av = col.sortValue(a);
      const bv = col.sortValue(b);
      if (av < bv) return -1 * factor;
      if (av > bv) return 1 * factor;
      return 0;
    });
  }, [posts, postSort]);

  const sentimentCounts = useMemo(() => {
    const counts = { positive: 0, neutral: 0, negative: 0, unclassified: 0 };
    for (const c of comments) {
      if (c.sentiment === "positive")  counts.positive++;
      else if (c.sentiment === "neutral")  counts.neutral++;
      else if (c.sentiment === "negative") counts.negative++;
      else counts.unclassified++;
    }
    return counts;
  }, [comments]);

  // Platform chart layout: switch to horizontal bars (categorical Y-axis)
  // when there are >5 platforms so the labels don't squish on the X-axis.
  // Height grows ~36px per row, capped between 200 and 480.
  const platformLayout = platforms.length > 5 ? "horizontal" : "vertical";
  const platformChartHeight =
    platformLayout === "horizontal"
      ? Math.max(200, Math.min(480, platforms.length * 36 + 80))
      : 260;

  // "Brands engagement over time" — one row per day, one numeric key per brand
  // holding that brand's total engagement (likes + comments + shares) so each
  // brand gets its own line.
  const brandEngagementChartData = useMemo(
    () => (brandTimeline.series || []).map((row) => {
      const out = { date: row.date };
      for (const b of brandTimeline.brands || []) {
        const v = (row.byBrand || {})[b.brandId];
        out[b.brandId] = v ? (v.likes || 0) + (v.comments || 0) + (v.shares || 0) : 0;
      }
      return out;
    }),
    [brandTimeline]
  );

  // "Types of engagement per brand over time" — likes/comments/shares lines for
  // the single brand the <select> is focused on.
  const brandTypesChartData = useMemo(
    () => (brandTimeline.series || []).map((row) => {
      const v = (selectedBrandId && (row.byBrand || {})[selectedBrandId]) || {};
      return {
        date: row.date,
        likes: v.likes || 0,
        comments: v.comments || 0,
        shares: v.shares || 0,
      };
    }),
    [brandTimeline, selectedBrandId]
  );

  // Both over-time charts share the per-point ~50px width + horizontal scroll
  // behaviour of the per-post chart so a long window stays readable.
  const brandTimelineMinWidth = useMemo(
    () => Math.max(0, (brandTimeline.series || []).length * 50),
    [brandTimeline]
  );

  // KPI strip totals — computed across whatever the current scope returned,
  // so the numbers stay coherent with the tables below without an extra
  // round-trip.
  const kpis = useMemo(() => {
    const totals = posts.reduce((acc, p) => {
      const a = p.analytics || {};
      acc.likes += a.likes || 0;
      acc.comments += a.comments || 0;
      acc.shares += a.shares || 0;
      acc.score += a.score || 0;
      return acc;
    }, { likes: 0, comments: 0, shares: 0, score: 0 });
    const engagement = totals.likes + totals.comments + totals.shares;
    const avgScore = posts.length > 0 ? Math.round(totals.score / posts.length) : 0;
    // Top engager = the highest-count commenter we can actually name. Rows are
    // already count-desc from the backend, so pick the first with a real
    // display name/handle rather than blindly taking [0] (which could be an
    // unresolved "(unknown)" row and made the card read "(unknown) · 1").
    const topEngager =
      engagedCommenters.find((e) => (e.displayName || e.handle || "").trim())
      || engagedCommenters[0];
    return {
      postsAnalyzed: postsAnalyzed,
      postsTotal: postsTotal || posts.length,
      engagement,
      avgScore,
      topEngagerLabel: topEngager && (topEngager.displayName || topEngager.handle)
        ? `${topEngager.displayName || topEngager.handle} · ${topEngager.commentCount}`
        : null,
    };
  }, [posts, postsTotal, postsAnalyzed, engagedCommenters]);

  return (
    <div data-testid="post-analytics-page" style={{ background: "#eaeaea" }}>
      {/* Header bar — same shape as Dashboard's */}
      <div className="inner-header-position">
        <div className="custom-container d-flex align-items-center justify-content-between flex-wrap">
          <div className="d-flex align-items-center">
            <span
              style={{ alignItems: "center", display: "flex", cursor: "pointer" }}
              onClick={() => dispatch(setSidebarStatus(true))}
            >
              <div className="cursor-pointer">
                <img src={Arrow} className="left-arrow-img" alt="" />
              </div>
              <p className="my-heading f-14 ml-3 mr-3">Side Menu</p>
            </span>
          </div>
          <div className="projActionButtons d-flex flex-wrap" style={{ maxWidth: "100vw" }}>
            <div className="btn-tool-group">
              <button
                disabled={loading}
                onClick={loadAll}
                className="px-3 bg-white text-bid shadow-hover"
              >
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="d-flex flex-column flex-lg-row h-lg-full bg-surface-secondary mx-3">
        <div className="h-screen flex-grow-1 overflow-y-lg-auto">
          <main className="py-6 bg-surface-secondary">
            <div className="container-fluid">
              {/* Title row */}
              <div className="row mt-3">
                <div className="col-12">
                  <h2 style={{ margin: 0, fontSize: 22 }}>Post Analytics</h2>
                  <div style={{ fontSize: 13, color: "#64748b" }}>
                    {kpis.postsCount} post{kpis.postsCount === 1 ? "" : "s"} in scope
                  </div>
                </div>
              </div>

              {error && (
                <div className="row mt-3">
                  <div className="col-12">
                    <div className="alert alert-danger">{error}</div>
                  </div>
                </div>
              )}

              {loading && !posts.length && (
                <div className="row mt-3">
                  <div className="col-12">
                    <div className="card border-0">
                      <div className="card-body">Loading post analytics…</div>
                    </div>
                  </div>
                </div>
              )}

              {/* KPI cards */}
              <div className="row g-3 mt-3">
                <div className="col-xl-3 col-sm-6 col-12 mb-3">
                  <StatCard
                    label="Posts analyzed"
                    value={kpis.postsAnalyzed}
                    accent="#0F62FE"
                    sub={`of ${kpis.postsTotal} published`}
                  />
                </div>
                <div className="col-xl-3 col-sm-6 col-12 mb-3">
                  <StatCard label="Total engagement" value={kpis.engagement} accent="#22c55e" sub="likes + comments + shares" />
                </div>
                <div className="col-xl-3 col-sm-6 col-12 mb-3">
                  <StatCard label="Avg score / post" value={kpis.avgScore} accent="#a855f7" sub="weighted: L×1 C×3 S×5" />
                </div>
                <div className="col-xl-3 col-sm-6 col-12 mb-3">
                  <StatCard label="Top engager" value={kpis.topEngagerLabel || "—"} accent="#f59e0b" sub="(last 14 days)" />
                </div>
              </div>

              {/* Top brands by engagement — every brand ranked, top on the left
                  with a green hue → least-engaging on the right with a red hue,
                  the proportional gradient overlaid in between. */}
              <div className="row g-3 mt-1">
                <div className="col-12 mb-3" data-testid="top-brand-kpi">
                  <div className="card border-0">
                    <div className="card-body">
                      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap" style={{ gap: 8 }}>
                        <p className="chart-header m-0" style={{ fontSize: 14, fontWeight: 600 }}>
                          Top brands by engagement
                          {rankPlatform !== "all" && (
                            <span style={{ fontWeight: 500, color: "#64748b" }}> · {rankPlatform}</span>
                          )}
                        </p>
                        {/* Overall / per-platform lens. Only platforms with data are offered. */}
                        <div className="btn-group btn-group-sm" role="group" aria-label="Ranking platform">
                          <button
                            type="button"
                            className={`btn btn-sm ${rankPlatform === "all" ? "btn-primary" : "btn-outline-secondary"}`}
                            onClick={() => setRankPlatform("all")}
                            disabled={rankLoading}
                          >
                            Overall
                          </button>
                          {rankPlatforms.map((rp) => (
                            <button
                              key={rp.platform}
                              type="button"
                              className={`btn btn-sm ${rankPlatform === rp.platform ? "btn-primary" : "btn-outline-secondary"}`}
                              onClick={() => setRankPlatform(rp.platform)}
                              disabled={rankLoading}
                              title={`${rp.posts} post${rp.posts === 1 ? "" : "s"} measured`}
                              style={{ textTransform: "capitalize" }}
                            >
                              {rp.platform}
                            </button>
                          ))}
                        </div>
                      </div>
                      {rankLoading ? (
                        <p className="text-muted m-0">Loading {rankPlatform} ranking…</p>
                      ) : brands.length === 0 ? (
                        <p className="text-muted m-0">No per-brand engagement data yet.</p>
                      ) : (
                        <div
                          data-testid="brand-rank-list"
                          style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 4 }}
                        >
                          {brands.map((b, i) => (
                            <BrandRankCard key={b.brandId || i} brand={b} rank={i} total={brands.length} />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Top posts table — full width */}
              <div className="row g-3 mt-1">
                <div className="col-12 mb-3">
                  <TableCard title="Top posts by engagement" maxHeight={360}>
                    {posts.length === 0 ? (
                      <p className="text-muted m-0">
                        No posts with engagement data yet. The worker refreshes engagement
                        counts daily — check back after the first refresh runs.
                      </p>
                    ) : (
                      <table className="table table-sm mb-0" data-testid="top-posts-table" style={{ fontSize: 13 }}>
                        <thead>
                          <tr>
                            {POST_COLUMNS.map((col) => {
                              const active = postSort.key === col.key;
                              return (
                                <th
                                  key={col.key}
                                  className={col.numeric ? "text-right" : undefined}
                                  onClick={() => handlePostSort(col.key)}
                                  data-testid={`post-sort-${col.key}`}
                                  title="Sort by this column"
                                  style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}
                                >
                                  {col.label}
                                  <span style={{ marginLeft: 4, fontSize: 11, color: active ? "#0F62FE" : "#cbd5e1" }}>
                                    {active ? (postSort.dir === "asc" ? "▲" : "▼") : "↕"}
                                  </span>
                                </th>
                              );
                            })}
                          </tr>
                        </thead>
                        <tbody>
                          {sortedPosts.map((p) => (
                            <tr
                              key={p._id}
                              onClick={() => setSelectedPostId(p._id)}
                              style={{
                                cursor: "pointer",
                                background: p._id === selectedPostId ? "#eef2ff" : undefined,
                              }}
                              data-testid={`post-row-${p._id}`}
                            >
                              <td style={{ maxWidth: 320, wordBreak: "break-word" }}>
                                {p.title || (p.text || "").slice(0, 60)}
                              </td>
                              <td style={{ maxWidth: 160, wordBreak: "break-word" }}>{p.brandName || "—"}</td>
                              <td style={{ maxWidth: 220, wordBreak: "break-word" }}>{p.ideaTitle || "—"}</td>
                              <td>{(p.platforms || []).join(", ")}</td>
                              <td className="text-right">{p.analytics?.likes ?? 0}</td>
                              <td className="text-right">{p.analytics?.comments ?? 0}</td>
                              <td className="text-right">{p.analytics?.shares ?? 0}</td>
                              <td className="text-right"><strong>{p.analytics?.score ?? 0}</strong></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </TableCard>
                </div>
              </div>

              {/* Engagement-over-time + comments side by side */}
              <div className="row g-3 mt-1">
                <div className="col-xl-6 col-12 mb-3">
                  <ChartCard
                    title={
                      <span className="d-flex align-items-center justify-content-between flex-wrap" style={{ gap: 8 }}>
                        <span>
                          {selectedPost
                            ? `Engagement over time — ${selectedPost.title || "(untitled)"}`
                            : "Engagement over time"}
                        </span>
                        {historyPlatforms.length > 0 && (
                          <select
                            data-testid="history-platform-filter"
                            value={platformFilter}
                            onChange={(e) => setPlatformFilter(e.target.value)}
                            style={{ fontSize: 12, padding: "2px 6px", borderRadius: 4, border: "1px solid #cbd5e1" }}
                          >
                            <option value="all">All platforms</option>
                            {historyPlatforms.map((p) => (
                              <option key={p} value={p}>{p}</option>
                            ))}
                          </select>
                        )}
                      </span>
                    }
                    height={280}
                  >
                    {historyChartData.length === 0 ? (
                      <p className="text-muted m-0" style={{ fontSize: 13 }}>
                        {selectedPost
                          ? "No snapshots yet for this post — chart fills in after the daily refresh runs."
                          : "Click a post above to load its engagement history."}
                      </p>
                    ) : (
                      <div style={{ width: "100%", height: "100%", overflowX: "auto" }}>
                        <div style={{ minWidth: historyMinWidth || "100%", height: "100%" }}>
                          <ResponsiveContainer>
                            <LineChart data={historyChartData} margin={{ top: 8, right: 16, bottom: 24, left: 0 }}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis
                                dataKey="date"
                                angle={-30}
                                textAnchor="end"
                                height={50}
                                interval="preserveStartEnd"
                                minTickGap={20}
                                tick={{ fontSize: 11 }}
                              />
                              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                              <Tooltip />
                              <Legend />
                              <Line type="monotone" dataKey="likes"    stroke="#0F62FE" dot={false} />
                              <Line type="monotone" dataKey="comments" stroke="#22c55e" dot={false} />
                              <Line type="monotone" dataKey="shares"   stroke="#ef4444" dot={false} />
                              <Line type="monotone" dataKey="score"    stroke="#a855f7" dot={false} strokeWidth={2} />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}
                  </ChartCard>
                </div>

                <div className="col-xl-6 col-12 mb-3">
                  <div className="card border-0 mb-3">
                    <div className="card-body">
                      <p className="chart-header mb-2" style={{ fontSize: 14, fontWeight: 600 }}>
                        {selectedPost ? `Comments — ${selectedPost.title || "(untitled)"}` : "Comments"}
                      </p>
                      {!selectedPost ? (
                        <p className="text-muted m-0" style={{ fontSize: 13 }}>Click a post above to see its comments.</p>
                      ) : comments.length === 0 ? (
                        <p className="text-muted m-0" style={{ fontSize: 13 }}>
                          No comments yet for this post — comment bodies are pulled by the
                          daily worker after publishing.
                        </p>
                      ) : (
                        <>
                          {(sentimentCounts.positive + sentimentCounts.neutral + sentimentCounts.negative) > 0 && (
                            <div
                              style={{ display: "flex", gap: 8, marginBottom: 8, fontSize: 12, flexWrap: "wrap" }}
                              data-testid="sentiment-summary"
                            >
                              {sentimentCounts.positive > 0 && (
                                <span style={{ background: "#dcfce7", color: "#166534", padding: "2px 8px", borderRadius: 4 }}>
                                  {sentimentCounts.positive} positive
                                </span>
                              )}
                              {sentimentCounts.neutral > 0 && (
                                <span style={{ background: "#e2e8f0", color: "#475569", padding: "2px 8px", borderRadius: 4 }}>
                                  {sentimentCounts.neutral} neutral
                                </span>
                              )}
                              {sentimentCounts.negative > 0 && (
                                <span style={{ background: "#fee2e2", color: "#991b1b", padding: "2px 8px", borderRadius: 4 }}>
                                  {sentimentCounts.negative} negative
                                </span>
                              )}
                              {sentimentCounts.unclassified > 0 && (
                                <span style={{ background: "#f1f5f9", color: "#64748b", padding: "2px 8px", borderRadius: 4 }}>
                                  {sentimentCounts.unclassified} unclassified
                                </span>
                              )}
                            </div>
                          )}
                          <ul
                            className="list-unstyled m-0"
                            data-testid="comments-list"
                            style={{ maxHeight: 280, overflowY: "auto" }}
                          >
                            {comments.map((c, idx) => (
                              <li
                                key={`${c.platform}-${idx}-${c.postedAt || ""}`}
                                style={{
                                  borderBottom: "1px solid #e2e8f0",
                                  padding: "10px 0",
                                  display: "flex",
                                  gap: 10,
                                }}
                                data-testid="comment-row"
                              >
                                {c.commenterAvatar ? (
                                  <img
                                    src={c.commenterAvatar}
                                    alt=""
                                    style={{ width: 36, height: 36, borderRadius: "50%", flexShrink: 0 }}
                                  />
                                ) : (
                                  <div
                                    style={{
                                      width: 36,
                                      height: 36,
                                      borderRadius: "50%",
                                      background: "#e2e8f0",
                                      flexShrink: 0,
                                    }}
                                  />
                                )}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: 13, fontWeight: 600 }}>
                                    {c.commenterName || c.commenterHandle || "(unknown)"}
                                    <span style={{ fontWeight: 400, color: "#64748b", marginLeft: 8 }}>
                                      {c.platform}
                                      {c.postedAt && ` · ${shortDate(c.postedAt)}`}
                                    </span>
                                    {c.sentiment && <SentimentBadge sentiment={c.sentiment} intent={c.intent} />}
                                  </div>
                                  <div style={{ fontSize: 14, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                    {c.body || "(empty)"}
                                  </div>
                                  {c.likes > 0 && (
                                    <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>
                                      {c.likes} like{c.likes === 1 ? "" : "s"}
                                    </div>
                                  )}
                                </div>
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Idea rankings + platform chart side by side */}
              <div className="row g-3 mt-1">
                <div className="col-xl-6 col-12 mb-3">
                  <TableCard title="Ideas ranked by total engagement" maxHeight={340}>
                    {ideas.length === 0 ? (
                      <p className="text-muted m-0">No idea-engagement data yet.</p>
                    ) : (
                      <table className="table table-sm mb-0" data-testid="ideas-table" style={{ fontSize: 13 }}>
                        <thead>
                          <tr>
                            <th>Idea</th>
                            <th className="text-right">Posts</th>
                            <th className="text-right">Likes</th>
                            <th className="text-right">Comments</th>
                            <th className="text-right">Shares</th>
                            <th className="text-right">Total score</th>
                            <th className="text-right">Avg / post</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ideas.map((i) => (
                            <tr key={i.ideaId}>
                              <td style={{ maxWidth: 240, wordBreak: "break-word" }}>{i.title || "(untitled)"}</td>
                              <td className="text-right">{i.postCount}</td>
                              <td className="text-right">{i.likes}</td>
                              <td className="text-right">{i.comments}</td>
                              <td className="text-right">{i.shares}</td>
                              <td className="text-right"><strong>{i.score}</strong></td>
                              <td className="text-right">{i.avgScore}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </TableCard>
                </div>

                <div className="col-xl-6 col-12 mb-3">
                  <ChartCard title="Platforms ranked by engagement" height={platformChartHeight}>
                    {platforms.length === 0 ? (
                      <p className="text-muted m-0" style={{ fontSize: 13 }}>No platform-engagement data yet.</p>
                    ) : (
                      <ResponsiveContainer>
                        <BarChart
                          data={platforms}
                          layout={platformLayout}
                          margin={{ top: 8, right: 16, bottom: 8, left: platformLayout === "horizontal" ? 16 : 0 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          {platformLayout === "horizontal" ? (
                            <>
                              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                              <YAxis type="category" dataKey="platform" width={90} tick={{ fontSize: 11 }} />
                            </>
                          ) : (
                            <>
                              <XAxis dataKey="platform" tick={{ fontSize: 11 }} />
                              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                            </>
                          )}
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="score"     fill="#0F62FE" name="Total score" />
                          <Bar dataKey="avgScore"  fill="#22c55e" name="Avg / post" />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </ChartCard>
                </div>
              </div>

              {/* Per-brand analytics: ranking table + brands-over-time chart */}
              <div className="row g-3 mt-1">
                <div className="col-xl-6 col-12 mb-3">
                  <TableCard title="Analytics per brand — posts & engagement" maxHeight={340}>
                    {brands.length === 0 ? (
                      <p className="text-muted m-0">No per-brand engagement data yet.</p>
                    ) : (
                      <table className="table table-sm mb-0" data-testid="brands-table" style={{ fontSize: 13 }}>
                        <thead>
                          <tr>
                            <th>Brand</th>
                            <th className="text-right">Posts</th>
                            <th className="text-right">Likes</th>
                            <th className="text-right">Comments</th>
                            <th className="text-right">Shares</th>
                            <th className="text-right">Total score</th>
                            <th className="text-right">Avg / post</th>
                          </tr>
                        </thead>
                        <tbody>
                          {brands.map((b) => (
                            <tr key={b.brandId} data-testid={`brand-row-${b.brandId}`}>
                              <td style={{ maxWidth: 220, wordBreak: "break-word" }}>{b.name || "(unknown brand)"}</td>
                              <td className="text-right">{b.postCount}</td>
                              <td className="text-right">{b.likes}</td>
                              <td className="text-right">{b.comments}</td>
                              <td className="text-right">{b.shares}</td>
                              <td className="text-right"><strong>{b.score}</strong></td>
                              <td className="text-right">{b.avgScore}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </TableCard>
                </div>

                <div className="col-xl-6 col-12 mb-3">
                  <ChartCard title="Brands engagement over time" height={280}>
                    {brandEngagementChartData.length === 0 ? (
                      <p className="text-muted m-0" style={{ fontSize: 13 }}>
                        No brand engagement history yet — fills in after the daily refresh writes snapshots.
                      </p>
                    ) : (
                      <div style={{ width: "100%", height: "100%", overflowX: "auto" }}>
                        <div style={{ minWidth: brandTimelineMinWidth || "100%", height: "100%" }}>
                          <ResponsiveContainer>
                            <LineChart data={brandEngagementChartData} margin={{ top: 8, right: 16, bottom: 24, left: 0 }}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis
                                dataKey="date"
                                angle={-30}
                                textAnchor="end"
                                height={50}
                                interval="preserveStartEnd"
                                minTickGap={20}
                                tick={{ fontSize: 11 }}
                              />
                              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                              <Tooltip />
                              <Legend />
                              {(brandTimeline.brands || []).map((b, idx) => (
                                <Line
                                  key={b.brandId}
                                  type="monotone"
                                  dataKey={b.brandId}
                                  name={b.name || "(unknown brand)"}
                                  stroke={BRAND_COLORS[idx % BRAND_COLORS.length]}
                                  dot={false}
                                />
                              ))}
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}
                  </ChartCard>
                </div>
              </div>

              {/* Types of engagement per brand over time (brand-filtered) */}
              <div className="row g-3 mt-1">
                <div className="col-12 mb-3">
                  <ChartCard
                    title={
                      <span className="d-flex align-items-center justify-content-between flex-wrap" style={{ gap: 8 }}>
                        <span>Types of engagement per brand over time</span>
                        {(brandTimeline.brands || []).length > 0 && (
                          <select
                            data-testid="brand-types-filter"
                            value={selectedBrandId || ""}
                            onChange={(e) => setSelectedBrandId(e.target.value)}
                            style={{ fontSize: 12, padding: "2px 6px", borderRadius: 4, border: "1px solid #cbd5e1" }}
                          >
                            {(brandTimeline.brands || []).map((b) => (
                              <option key={b.brandId} value={b.brandId}>{b.name || "(unknown brand)"}</option>
                            ))}
                          </select>
                        )}
                      </span>
                    }
                    height={280}
                  >
                    {brandTypesChartData.length === 0 || !selectedBrandId ? (
                      <p className="text-muted m-0" style={{ fontSize: 13 }}>
                        No per-brand engagement history yet — pick a brand once snapshots exist.
                      </p>
                    ) : (
                      <div style={{ width: "100%", height: "100%", overflowX: "auto" }}>
                        <div style={{ minWidth: brandTimelineMinWidth || "100%", height: "100%" }}>
                          <ResponsiveContainer>
                            <LineChart data={brandTypesChartData} margin={{ top: 8, right: 16, bottom: 24, left: 0 }}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis
                                dataKey="date"
                                angle={-30}
                                textAnchor="end"
                                height={50}
                                interval="preserveStartEnd"
                                minTickGap={20}
                                tick={{ fontSize: 11 }}
                              />
                              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                              <Tooltip />
                              <Legend />
                              <Line type="monotone" dataKey="likes"    stroke="#0F62FE" name="Likes"    dot={false} />
                              <Line type="monotone" dataKey="comments" stroke="#22c55e" name="Comments" dot={false} />
                              <Line type="monotone" dataKey="shares"   stroke="#ef4444" name="Shares"   dot={false} />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}
                  </ChartCard>
                </div>
              </div>

              {/* Top engagers */}
              <div className="row g-3 mt-1">
                <div className="col-12 mb-3">
                  <TableCard title="Top engagers (last 14 days)" maxHeight={360}>
                    {engagedCommenters.length === 0 ? (
                      <p className="text-muted m-0">
                        No engaged commenters yet — populates after the daily comment refresh runs.
                      </p>
                    ) : (
                      <table className="table table-sm mb-0" data-testid="engaged-commenters-table" style={{ fontSize: 13 }}>
                        <thead>
                          <tr>
                            <th />
                            <th>Name</th>
                            <th>Handle</th>
                            <th>Platform</th>
                            <th className="text-right">Comments</th>
                            <th className="text-right">Posts</th>
                            <th>Last seen</th>
                          </tr>
                        </thead>
                        <tbody>
                          {engagedCommenters.map((c) => (
                            <tr key={c.key || `${c.platform}-${c.commenterId}`}>
                              <td style={{ width: 40 }}>
                                {c.avatarUrl ? (
                                  <img
                                    src={c.avatarUrl}
                                    alt=""
                                    style={{ width: 28, height: 28, borderRadius: "50%" }}
                                  />
                                ) : (
                                  <div
                                    style={{
                                      width: 28,
                                      height: 28,
                                      borderRadius: "50%",
                                      background: "#e2e8f0",
                                    }}
                                  />
                                )}
                              </td>
                              <td style={{ maxWidth: 200, wordBreak: "break-word" }}>
                                {c.profileUrl ? (
                                  <a href={c.profileUrl} target="_blank" rel="noreferrer">
                                    {c.displayName || "(unknown)"}
                                  </a>
                                ) : (
                                  c.displayName || "(unknown)"
                                )}
                              </td>
                              <td>{c.handle ? `@${c.handle}` : "—"}</td>
                              <td>{c.platform}</td>
                              <td className="text-right"><strong>{c.commentCount}</strong></td>
                              <td className="text-right">{c.postCount}</td>
                              <td>{shortDate(c.lastSeenAt)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </TableCard>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
